import { useCallback, useEffect, useLayoutEffect, useRef, useState, type JSX, type MouseEvent, type PointerEvent, type ReactNode, type RefObject } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  useDndContext,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { api } from '../lib/api.ts';
import { useDictation } from '../lib/dictation.ts';
import { live } from '../lib/live.ts';
import { stashDroppedImages, useImageDrop } from '../lib/drop.ts';
import { buzz, countDoneTasks, countDoneTasksOther, doneTasksByProject, isWaitingStatus, notifyRunFinished, rootScroller, runJustFinished, useDevServer, useGitDiffstat, useLiveStatus, useNow } from '../lib/hooks.ts';
import { Icon } from '../lib/icons.tsx';
import { useEnabledModelIds } from '../lib/model-catalog.ts';
import { rotateSessionPrefs, type PrefAxis } from '../lib/models.ts';
import { sessionHasTask } from '../lib/protocol.ts';
import { moveIndex, upsertSession } from '../lib/session-order.ts';
import { displayHost, readStoredProject, resolveProjectId, storeProject, type ProjectRow } from '../lib/project.ts';
import type { DeploySnapshot, GitSnapshot, Session, UsageResponse } from '../lib/protocol.ts';
import { mergeUsage, readStoredUsage, storeUsage } from '../lib/usage.ts';
import { BacklogView } from './BacklogView.tsx';
import {
  CommandsView,
  ConfigSubTabs,
  ModelsConfigView,
  ProjectsConfigView,
  UpdatesConfigView,
  VisionView,
  type ConfigSubTab,
} from './ConfigView.tsx';
import { FilesView } from './FilesView.tsx';
import { GitView, GitSubTabs, gitSubtitle, Diffstat, type GitSubTab } from './GitView.tsx';
import { SessionContents } from './SessionRow.tsx';
import { SessionTabs, type SessionTab } from './SessionTabs.tsx';
import { UsageCard } from './UsageCard.tsx';

/** Width of one revealed swipe action, and the snap target after a left swipe. */
const ACTION = 80;
const HOME_ACTIONS = ACTION * 2;
/** Right-edge strip where a left swipe opens the backlog even on top of a row. */
const SHEET_EDGE = 48;
/** Pixels before a sheet drag locks as horizontal. */
const SHEET_SLOP = 8;
/** Horizontal px/ms that counts as a flick to open or close the sheet. */
const SHEET_FLICK = 0.22;
/** Fraction of sheet width you must travel to snap without a flick. */
const SHEET_COMMIT = 0.28;

export type HomeTab = 'home' | 'git' | 'config';

export function SessionList({
  tab,
  onOpen,
  onTab,
}: {
  tab: HomeTab;
  onOpen: (sessionId: string, tab?: Extract<SessionTab, 'chat' | 'task'>) => void;
  onTab: (tab: HomeTab) => void;
}): JSX.Element {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [projectId, setProjectId] = useState<string | null>(readStoredProject);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [usage, setUsage] = useState<UsageResponse | null>(readStoredUsage);
  const [git, setGit] = useState<GitSnapshot | null>(null);
  const [gitError, setGitError] = useState<string | null>(null);
  const [gitBusy, setGitBusy] = useState(false);
  const [gitSubTab, setGitSubTab] = useState<GitSubTab>('worktree');
  const [configSubTab, setConfigSubTab] = useState<ConfigSubTab>('vision');
  const [deploy, setDeploy] = useState<DeploySnapshot | null>(null);
  const [deployBusy, setDeployBusy] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [pushError, setPushError] = useState<string | null>(null);
  const [deployError, setDeployError] = useState<string | null>(null);
  const [blenderBusy, setBlenderBusy] = useState(false);
  const blenderBusyRef = useRef(false);
  const status = useLiveStatus();
  const enabledModels = useEnabledModelIds();
  const backlogSheet = useSlideSheet();
  const selectedId = resolveProjectId(projects, projectId);
  const selected = projects.find((p) => p.id === selectedId) ?? null;
  const showCommands = Boolean(selected?.desktop && selected.desktopOnline);
  const connectedDesktop = (selected?.desktopOnline ? selected.desktop?.host : undefined)
    ?? projects.find((p) => p.desktopOnline && p.desktop)?.desktop?.host;
  const diffstat = useGitDiffstat(selectedId);
  const devServer = useDevServer(selected);
  // Same gate as the Commands tab: no desktop on the line, nothing to toggle.
  const canRunDev = showCommands && devServer.command !== null;
  const canDeploy = Boolean(selected?.deploy?.trim());
  const showBlender = selected?.id === 'physical';
  const deploying = deployBusy || deploy?.status === 'running';
  const canPush = git !== null && !git.detached && git.upstream !== null && git.ahead > 0;
  const pushTitle = !git
    ? 'Push'
    : git.detached
      ? 'Detached HEAD'
      : !git.upstream
        ? 'No upstream configured'
        : git.ahead === 0
          ? 'Nothing to push'
          : `Push ${git.ahead} commit${git.ahead === 1 ? '' : 's'} to ${git.upstream}`;
  const phrasesRef = useRef<string[]>([]);
  const [phrases, setPhrases] = useState<string[]>([]);
  const [savingVoice, setSavingVoice] = useState(false);
  const savingVoiceRef = useRef(false);
  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;
  const sessionsRef = useRef(sessions);
  sessionsRef.current = sessions;
  const draggingRef = useRef(false);
  const sensors = useSensors(useSensor(PointerSensor));

  const fileVoiceTask = useCallback(async (): Promise<void> => {
    const text = phrasesRef.current.join(' ').replace(/\s+/g, ' ').trim();
    phrasesRef.current = [];
    setPhrases([]);
    const projectId = selectedIdRef.current;
    if (!text) {
      setError("Didn't catch that. Tap the mic and try again.");
      return;
    }
    if (!projectId) return;
    savingVoiceRef.current = true;
    setSavingVoice(true);
    setError(null);
    try {
      const session = await api.createTask(projectId, text);
      setSessions((prev) => upsertSession(prev, session));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      savingVoiceRef.current = false;
      setSavingVoice(false);
    }
  }, []);

  const dictation = useDictation(
    (phrase) => {
      phrasesRef.current = [...phrasesRef.current, phrase];
      setPhrases(phrasesRef.current);
    },
    () => void fileVoiceTask(),
  );

  useEffect(() => {
    if (tab !== 'home' && dictation.listening) dictation.cancel();
  }, [tab, dictation.listening]);

  const onVoiceFab = (): void => {
    if (savingVoiceRef.current || dictation.stopping) return;
    if (!dictation.listening) {
      phrasesRef.current = [];
      setPhrases([]);
    }
    dictation.toggle();
  };

  const heard = [...phrases, dictation.interim].filter(Boolean).join(' ');
  const voiceLive = dictation.listening || dictation.stopping || savingVoice;
  const homeFabCount =
    2 + (canDeploy ? 1 : 0) + (canRunDev ? 1 : 0) + (dictation.available ? 1 : 0) + (showBlender ? 1 : 0);
  const listFabClass =
    homeFabCount >= 6
      ? ' list-has-6-fabs'
      : homeFabCount >= 5
      ? ' list-has-5-fabs'
      : homeFabCount >= 4
        ? ' list-has-4-fabs'
        : homeFabCount >= 3
          ? ' list-has-3-fabs'
          : homeFabCount >= 2
            ? ' list-has-fabs'
            : ' list-has-fab';
  const doneByProject = doneTasksByProject(sessions);
  const visible = selectedId
    ? sessions.filter((s) => s.projectId === selectedId && !s.backlogged)
    : [];
  const backloggedCount = selectedId
    ? sessions.filter((s) => s.projectId === selectedId && s.backlogged).length
    : 0;
  const showBacklog = tab === 'config' && configSubTab === 'backlog';
  const visibleIds = visible.map((s) => s.id);
  const canReorder = visible.length > 1;
  const activeSession = activeId ? visible.find((s) => s.id === activeId) ?? null : null;
  const waiting = visible.some((s) => isWaitingStatus(s.runStatus));
  const now = useNow(waiting);

  useEffect(() => {
    rootScroller().scrollTop = 0;
  }, [tab, configSubTab]);

  useEffect(() => {
    void (async () => {
      try {
        const [s, p] = await Promise.all([api.sessions(), api.projects()]);
        setSessions(s);
        setProjects(p);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    })();
  }, []);

  useEffect(() => live.onDesktop(() => {
    void api.projects().then(setProjects).catch(() => undefined);
  }), []);

  useEffect(() => {
    if (projects.length === 0) return;
    const resolved = resolveProjectId(projects, projectId);
    if (resolved && resolved !== projectId) {
      storeProject(resolved);
      setProjectId(resolved);
    }
  }, [projects, projectId]);

  const loadGit = useCallback(async (): Promise<void> => {
    if (!selectedId) return;
    setGitBusy(true);
    try {
      setGit(await api.git(selectedId));
      setGitError(null);
    } catch (err) {
      setGitError(err instanceof Error ? err.message : String(err));
    } finally {
      setGitBusy(false);
    }
  }, [selectedId]);

  const loadDeploy = useCallback(async (): Promise<void> => {
    const id = selectedId;
    if (!id || !selected?.deploy) {
      setDeploy(null);
      return;
    }
    try {
      const state = await api.deployState(id);
      if (selectedIdRef.current !== id) return;
      setDeploy(state.snapshot);
    } catch {
      // Keep the last snapshot: a blip must not hide an in-flight deploy.
    }
  }, [selectedId, selected?.deploy]);

  useEffect(() => {
    if (!selectedId) {
      setGit(null);
      setGitError(null);
      setDeploy(null);
      return;
    }
    let cancelled = false;
    setGit(null);
    setGitError(null);
    setPushError(null);
    setDeployError(null);
    setGitBusy(true);
    void api
      .git(selectedId)
      .then((snap) => {
        if (cancelled) return;
        setGit(snap);
        setGitError(null);
      })
      .catch((err) => {
        if (!cancelled) setGitError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setGitBusy(false);
      });
    void loadDeploy();
    return () => {
      cancelled = true;
    };
  }, [selectedId, selected?.deploy, selected?.activeHost, loadDeploy]);

  useEffect(() => {
    if (!selectedId) return;
    return live.onDeploy((snap) => {
      if (snap.projectId === selectedId) setDeploy(snap);
    });
  }, [selectedId]);

  useEffect(() => {
    if (!selected?.deploy) return;
    let prev = live.status;
    return live.onStatus((status) => {
      if (prev !== 'live' && status === 'live') void loadDeploy();
      prev = status;
    });
  }, [selected?.deploy, loadDeploy]);

  const wasDeploying = useRef(false);
  useEffect(() => {
    if (wasDeploying.current && deploy && deploy.status !== 'running') {
      buzz(30);
      setDeployError(
        deploy.status === 'error'
          ? deploy.error
            ? `Deploy failed: ${deploy.error}`
            : 'Deploy failed'
          : null,
      );
    }
    wasDeploying.current = deploy?.status === 'running';
  }, [deploy]);

  const waitingRef = useRef(false);
  useEffect(() => {
    if (waitingRef.current && !waiting) void loadGit();
    waitingRef.current = waiting;
  }, [waiting, loadGit]);

  const runStatusById = useRef(new Map<string, string | null>());
  useEffect(() => {
    const prev = runStatusById.current;
    for (const s of sessions) {
      const before = prev.get(s.id);
      prev.set(s.id, s.runStatus);
      if (runJustFinished(before, s.runStatus)) notifyRunFinished();
    }
  }, [sessions]);

  useEffect(() => {
    const onVis = (): void => {
      if (document.visibilityState === 'visible') {
        void loadGit();
        void loadDeploy();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [loadGit, loadDeploy]);

  const commit = async (message: string): Promise<void> => {
    if (!selectedId) throw new Error('no project');
    setGit(await api.commit(selectedId, message));
  };

  const push = async (): Promise<void> => {
    if (!selectedId) throw new Error('no project');
    setGit(await api.push(selectedId));
  };

  const homePush = async (): Promise<void> => {
    if (pushing || !canPush) return;
    setPushing(true);
    setPushError(null);
    try {
      await push();
    } catch (err) {
      setPushError(err instanceof Error ? err.message : String(err));
    } finally {
      setPushing(false);
    }
  };

  const persistOrder = async (projectId: string, ids: string[]): Promise<void> => {
    try {
      const next = await api.reorderSessions(projectId, ids);
      setSessions((prev) => [...next, ...prev.filter((s) => s.projectId !== projectId)]);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      try {
        setSessions(await api.sessions());
      } catch {
        /* listed error is enough */
      }
    }
  };

  const onDragStart = (event: DragStartEvent): void => {
    setOpenId(null);
    draggingRef.current = true;
    setActiveId(String(event.active.id));
    buzz(15);
  };

  const onDragEnd = (event: DragEndEvent): void => {
    draggingRef.current = false;
    setActiveId(null);
    const projectId = selectedIdRef.current;
    const overId = event.over?.id;
    if (!projectId || overId == null || event.active.id === overId) return;
    const vis = sessionsRef.current.filter((s) => s.projectId === projectId && !s.backlogged);
    const from = vis.findIndex((s) => s.id === String(event.active.id));
    const to = vis.findIndex((s) => s.id === String(overId));
    if (from < 0 || to < 0 || from === to) return;
    const moved = moveIndex(vis, from, to);
    setSessions((prev) => [
      ...moved,
      ...prev.filter((s) => s.projectId !== projectId || s.backlogged),
    ]);
    void persistOrder(projectId, moved.map((s) => s.id));
  };

  const onDragCancel = (): void => {
    draggingRef.current = false;
    setActiveId(null);
  };

  useEffect(
    () =>
      live.onSession((updated) =>
        setSessions((prev) => {
          if (draggingRef.current) {
            const known = prev.some((s) => s.id === updated.id);
            return known
              ? prev.map((s) => (s.id === updated.id ? { ...updated, sortOrder: s.sortOrder } : s))
              : prev;
          }
          return upsertSession(prev, updated);
        }),
      ),
    [],
  );

  useEffect(() => {
    let cancelled = false;
    const load = (): void => {
      void api
        .usage()
        .then((next) => {
          if (cancelled) return;
          setUsage((prev) => {
            const merged = mergeUsage(prev, next);
            if (!(merged.cursor.ok || merged.claude.ok || merged.grok?.ok || merged.openai?.ok)) {
              return prev;
            }
            storeUsage(merged);
            return merged;
          });
        })
        .catch(() => {
          /* usage is optional; the list still works without it */
        });
    };
    load();
    const onVis = (): void => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  useEffect(() => {
    if (openId == null) return;
    const close = (): void => setOpenId(null);
    addEventListener('scroll', close, { passive: true });
    return () => removeEventListener('scroll', close);
  }, [openId]);

  const pickProject = (id: string): void => {
    storeProject(id);
    setProjectId(id);
    onTab('home');
  };

  const start = async (): Promise<void> => {
    if (!selectedId) return;
    setOpenId(null);
    try {
      const session = await api.createSession(selectedId);
      setSessions((prev) => upsertSession(prev, session));
      onOpen(session.id, 'task');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const startDeploy = async (): Promise<void> => {
    if (!selectedId || !canDeploy) return;
    setDeployBusy(true);
    setError(null);
    setDeployError(null);
    try {
      setDeploy(await api.startDeploy(selectedId));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setDeployBusy(false);
    }
  };

  const reloadBlender = async (): Promise<void> => {
    if (!showBlender || !selectedId || blenderBusyRef.current) return;
    blenderBusyRef.current = true;
    setBlenderBusy(true);
    setError(null);
    try {
      await api.reloadBlender(selectedId);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      blenderBusyRef.current = false;
      setBlenderBusy(false);
    }
  };

  const devHost = displayHost(selected?.desktop?.host ?? 'desktop');
  const devAction = devServer.command?.label ?? 'Dev server';
  const devServerTitle = devServer.running
    ? `Stop ${devAction} on ${devHost}${devServer.port ? `:${devServer.port}` : ''}`
    : `Start ${devAction} on ${devHost}`;

  const toggleDevServer = async (): Promise<void> => {
    setError(null);
    try {
      await devServer.toggle();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const rotatePrefs = async (sessionId: string, axis: PrefAxis): Promise<void> => {
    const session = sessionsRef.current.find((s) => s.id === sessionId);
    if (!session) return;
    const next = rotateSessionPrefs(session.model, session.effort, axis, enabledModels);
    setSessions((prev) =>
      prev.map((s) => (s.id === sessionId ? { ...s, model: next.model, effort: next.effort } : s)),
    );
    try {
      const updated = await api.updateSessionPrefs(sessionId, next.model, next.effort);
      setSessions((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
    } catch (err) {
      setSessions((prev) => prev.map((s) => (s.id === sessionId ? session : s)));
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const setSkipVerification = async (sessionId: string, skip: boolean): Promise<void> => {
    const session = sessionsRef.current.find((s) => s.id === sessionId);
    if (!session) return;
    setSessions((prev) =>
      prev.map((s) => (s.id === sessionId ? { ...s, skipVerification: skip } : s)),
    );
    try {
      const updated = await api.setSkipVerification(sessionId, skip);
      setSessions((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
    } catch (err) {
      setSessions((prev) => prev.map((s) => (s.id === sessionId ? session : s)));
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const setParallelTask = async (sessionId: string, parallel: boolean): Promise<void> => {
    const session = sessionsRef.current.find((s) => s.id === sessionId);
    if (!session) return;
    setSessions((prev) =>
      prev.map((s) => (s.id === sessionId ? { ...s, parallelTask: parallel } : s)),
    );
    try {
      const updated = await api.setParallelTask(sessionId, parallel);
      setSessions((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
    } catch (err) {
      setSessions((prev) => prev.map((s) => (s.id === sessionId ? session : s)));
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const runTask = async (session: Session): Promise<void> => {
    try {
      const updated = await api.runTask(
        session.id,
        session.model ?? undefined,
        session.effort,
      );
      setSessions((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const stopSession = async (id: string): Promise<void> => {
    try {
      const result = await api.cancel(id);
      if (result.session) {
        setSessions((prev) => prev.map((s) => (s.id === result.session.id ? result.session : s)));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const remove = async (id: string): Promise<void> => {
    try {
      await api.deleteSession(id);
      setSessions((prev) => prev.filter((s) => s.id !== id));
      setOpenId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setOpenId(null);
    }
  };

  const park = async (id: string): Promise<void> => {
    try {
      const updated = await api.setSessionBacklogged(id, true);
      setSessions((prev) => upsertSession(prev, updated));
      setOpenId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setOpenId(null);
    }
  };

  const closeSheet = backlogSheet.close;
  useEffect(() => {
    if (tab !== 'home') closeSheet();
  }, [tab, closeSheet]);

  const branchLabel = git ? gitSubtitle(git) : null;

  return (
    <div className={(!showBacklog && tab === 'config') || tab === 'git' ? 'screen screen-pinned' : 'screen'}>
      <header className="topbar">
        <div className="topbar-title">
          <div className="topbar-name">
            {selected?.name ?? 'Agents'}
          </div>
          <div className="topbar-sub" title={branchLabel ?? undefined}>
            {branchLabel ?? '\u00a0'}
          </div>
        </div>
        <div className="topbar-status">
          <Diffstat stat={diffstat} />
          <span className={`dot dot-${status}`} aria-label={status} />
        </div>
      </header>

      <div
        className={`list${tab === 'home' && selected?.exists ? listFabClass : ''}${activeId ? ' is-reordering' : ''}`}
        id="pane-home"
        role="tabpanel"
        aria-labelledby="tab-home"
        hidden={tab !== 'home'}
        onPointerDown={(e) => {
          if (openId != null && !(e.target instanceof Element && e.target.closest('.session-swipe'))) {
            setOpenId(null);
          }
          backlogSheet.paneHandlers.onPointerDown(e);
        }}
        onPointerMove={backlogSheet.paneHandlers.onPointerMove}
        onPointerUp={backlogSheet.paneHandlers.onPointerUp}
        onPointerCancel={backlogSheet.paneHandlers.onPointerCancel}
        onClickCapture={backlogSheet.paneHandlers.onClickCapture}
      >
        {usage && <UsageCard usage={usage} />}
        {error && <div className="note note-error">{error}</div>}
        {dictation.error && <div className="note note-error">{dictation.error}</div>}
        {deployError && <div className="note note-error">{deployError}</div>}
        {(pushError || git?.desktopError) && (
          <div className="note note-error">{pushError ?? git?.desktopError}</div>
        )}
        {projects.length === 0 && !error && (
          <div className="empty">
            <p>No projects configured.</p>
            <p className="empty-hint">
              Add repositories to <code>projects.json</code> on the server and pull to refresh.
            </p>
          </div>
        )}
        {selected && !selected.exists && visible.length === 0 && (
          <div className="empty">
            <p>Project path is missing.</p>
            <p className="empty-hint">
              <code>{selected.path}</code>
            </p>
          </div>
        )}
        {visible.length === 0 && selected?.exists && (
          <div className="empty">
            {backloggedCount > 0 ? (
              <>
                <p>No chats on Home.</p>
                <p className="empty-hint">Parked chats are in More → Backlog.</p>
              </>
            ) : (
              <>
                <p>No chats yet.</p>
                <p className="empty-hint">Tap + to start one in {selected.name}.</p>
              </>
            )}
          </div>
        )}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onDragCancel={onDragCancel}
        >
          <SortableContext items={visibleIds} strategy={verticalListSortingStrategy}>
            {visible.map((s) => (
              <SwipeRow
                key={s.id}
                id={s.id}
                open={openId === s.id}
                reorderable={canReorder}
                label={s.title}
                onOpen={() => setOpenId(s.id)}
                onClose={() => setOpenId((id) => (id === s.id ? null : id))}
                onBacklog={() => void park(s.id)}
                onDelete={() => void remove(s.id)}
                onDropImages={(files) => {
                  stashDroppedImages(s.id, 'chat', files);
                  onOpen(s.id, 'chat');
                }}
              >
                <SessionContents
                  session={s}
                  now={now}
                  onOpen={() => {
                    if (openId === s.id) {
                      setOpenId(null);
                      return;
                    }
                    onOpen(s.id, sessionHasTask(s) ? 'task' : 'chat');
                  }}
                  onRun={() => void runTask(s)}
                  onStop={() => void stopSession(s.id)}
                  onRotate={(axis) => void rotatePrefs(s.id, axis)}
                  onSkipVerification={(skip) => void setSkipVerification(s.id, skip)}
                  onParallelTask={(parallel) => void setParallelTask(s.id, parallel)}
                />
              </SwipeRow>
            ))}
          </SortableContext>
          <DragOverlay dropAnimation={null} zIndex={40}>
            {activeSession && <SessionOverlay session={activeSession} now={now} />}
          </DragOverlay>
        </DndContext>
      </div>

      {selectedId && (
        <GitView
          hidden={tab !== 'git'}
          projectId={selectedId}
          snap={git}
          error={gitError}
          busy={gitBusy}
          subTab={gitSubTab}
          onRefresh={() => void loadGit()}
          onCommit={commit}
          onPush={push}
          onSnapshot={setGit}
        />
      )}

      <BacklogView
        hidden={!showBacklog}
        projectId={selectedId}
        projectName={selected?.name}
      />

      {selected && (
        <VisionView
          hidden={tab !== 'config' || configSubTab !== 'vision'}
          projectId={selected.id}
          host={selected.activeHost}
          onOpen={onOpen}
        />
      )}

      {selected && (
        <FilesView
          hidden={tab !== 'config' || configSubTab !== 'files'}
          projectId={selected.id}
          host={selected.activeHost}
        />
      )}

      <ProjectsConfigView hidden={tab !== 'config' || configSubTab !== 'projects'} />

      <ModelsConfigView
        hidden={tab !== 'config' || configSubTab !== 'models'}
        desktopHost={connectedDesktop}
      />

      <UpdatesConfigView hidden={tab !== 'config' || configSubTab !== 'updates'} />

      {selected && (
        <CommandsView
          hidden={tab !== 'config' || configSubTab !== 'commands'}
          project={selected}
        />
      )}

      {tab === 'home' && (
        <div
          ref={backlogSheet.ref}
          className={`slide-sheet${backlogSheet.open ? ' is-open' : ''}`}
          aria-hidden={!backlogSheet.open}
          {...backlogSheet.sheetHandlers}
        >
          <div className="slide-sheet-handle" aria-hidden="true" />
          <BacklogView
            paneId="pane-backlog-home"
            labelledBy={null}
            projectId={selectedId}
            projectName={selected?.name}
          />
        </div>
      )}

      {tab === 'home' && selected?.exists && !backlogSheet.open && (
        <div className="fabs fabs-home">
          {showBlender && (
            <button
              type="button"
              className={`fab fab-quiet${blenderBusy ? ' fab-busy' : ''}`}
              onClick={() => void reloadBlender()}
              disabled={blenderBusy}
              aria-busy={blenderBusy}
              aria-label={blenderBusy ? 'Reloading Blender bridge' : 'Reload Blender bridge'}
              title={`Load and run blender/bridge.py from ${displayHost(selected.activeHost ?? 'server')} in Blender`}
            >
              <span className="fab-icon">
                <Icon name="refresh" size={22} strokeWidth={2.4} />
                {blenderBusy && (
                  <span className="fab-badge fab-badge-spin" aria-hidden="true"><span className="spinner" /></span>
                )}
              </span>
            </button>
          )}
          {canRunDev && (
            <button
              type="button"
              className={`fab fab-quiet${devServer.running ? ' fab-open' : ''}${devServer.busy ? ' fab-busy' : ''}`}
              onClick={() => void toggleDevServer()}
              disabled={devServer.busy}
              aria-busy={devServer.busy}
              aria-pressed={devServer.running}
              aria-label={devServer.running ? `Stop ${devAction}` : `Start ${devAction}`}
              title={devServerTitle}
            >
              <span className="fab-icon">
                <Icon name={devServer.running ? 'stop' : 'play'} size={22} strokeWidth={2.4} />
                {devServer.busy && (
                  <span className="fab-badge fab-badge-spin" aria-hidden="true">
                    <span className="spinner" />
                  </span>
                )}
              </span>
            </button>
          )}
          {canDeploy && (
            <button
              type="button"
              className={`fab fab-quiet${deploying ? ' fab-busy' : ''}`}
              onClick={() => void startDeploy()}
              disabled={deploying}
              aria-busy={deploying}
              aria-label={deploying ? 'Deploying' : 'Deploy'}
              title={deploying ? 'Deploy running' : `Deploy ${selected.name}`}
            >
              <span className="fab-icon">
                <Icon name="deploy" size={22} strokeWidth={2.4} />
                {deploying && (
                  <span className="fab-badge fab-badge-spin" aria-hidden="true">
                    <span className="spinner" />
                  </span>
                )}
              </span>
            </button>
          )}
          <button
            type="button"
            className="fab fab-quiet"
            onClick={() => void homePush()}
            disabled={pushing || !canPush}
            aria-label={
              git && git.ahead > 0
                ? `Push ${git.ahead} commit${git.ahead === 1 ? '' : 's'}`
                : 'Push'
            }
            title={pushTitle}
          >
            <Icon name="push" size={22} strokeWidth={2.4} />
            {git && git.ahead > 0 && (
              <span className="fab-badge" aria-hidden="true">
                {git.ahead}
              </span>
            )}
          </button>
          {dictation.available && (
            <button
              type="button"
              className={`fab fab-quiet${dictation.listening || dictation.stopping ? ' fab-listening' : ''}`}
              onClick={onVoiceFab}
              disabled={savingVoice || dictation.stopping}
              aria-label={
                dictation.listening
                  ? 'Stop recording'
                  : dictation.stopping || savingVoice
                    ? 'Saving task'
                    : 'New task from voice'
              }
              aria-pressed={dictation.listening}
            >
              <Icon name="mic" size={22} strokeWidth={2.4} />
            </button>
          )}
          <button type="button" className="fab" onClick={() => void start()} aria-label="New chat">
            <PlusIcon />
          </button>
        </div>
      )}
      {tab === 'home' && selected?.exists && !backlogSheet.open && voiceLive && (
        <div className="voice-task-banner" role="status">
          <span className="rec-dot" aria-hidden="true" />
          <span className="voice-task-text">
            {savingVoice
              ? 'Saving task...'
              : heard || (dictation.stopping ? 'Saving...' : 'Listening...')}
          </span>
        </div>
      )}

      <div className="dock">
        {tab === 'git' && selectedId && (
          <GitSubTabs tab={gitSubTab} dirty={git?.dirty === true} onTab={setGitSubTab} />
        )}
        {tab === 'config' && (
          <ConfigSubTabs tab={configSubTab} onTab={setConfigSubTab} showCommands={showCommands} />
        )}
        <SessionTabs
          tab={tab}
          inSession={false}
          doneTasks={countDoneTasks(sessions, selectedId)}
          doneTasksOther={countDoneTasksOther(sessions, selectedId)}
          gitDirty={git?.dirty === true}
          projects={projects}
          selectedProjectId={selectedId}
          doneByProject={doneByProject}
          onSelectProject={pickProject}
          onTab={(next) => {
            setOpenId(null);
            closeSheet();
            if (next === 'home' || next === 'git' || next === 'config') onTab(next);
          }}
        />
      </div>
    </div>
  );
}

type PaneGesture = {
  onPointerDown: (e: PointerEvent<HTMLDivElement>) => void;
  onPointerMove: (e: PointerEvent<HTMLDivElement>) => void;
  onPointerUp: (e: PointerEvent<HTMLDivElement>) => void;
  onPointerCancel: (e: PointerEvent<HTMLDivElement>) => void;
  onClickCapture: (e: MouseEvent<HTMLDivElement>) => void;
};

/**
 * Drag-to-open panel that rides in from the right edge of Home. A left swipe on
 * the list pulls it in under the finger, a right swipe inside it pushes it back.
 * Rows keep their own left swipe, so over a row only the right edge starts it.
 */
function useSlideSheet(): {
  open: boolean;
  close: () => void;
  ref: RefObject<HTMLDivElement | null>;
  paneHandlers: PaneGesture;
  sheetHandlers: PaneGesture;
} {
  const ref = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const seen = useRef(false);
  const suppressClick = useRef(false);
  const g = useRef({
    pointerId: -1,
    mode: 'idle' as 'idle' | 'pending' | 'h' | 'ignore',
    startX: 0,
    startY: 0,
    origin: 0,
    x: 0,
    lastX: 0,
    lastT: 0,
    vx: 0,
  });

  const width = (): number => ref.current?.offsetWidth || window.innerWidth || 360;

  const setX = (x: number, animate: boolean): void => {
    const el = ref.current;
    if (!el) return;
    g.current.x = x;
    el.style.transition = animate ? 'transform 0.22s ease-out' : 'none';
    el.style.transform = `translate3d(${x}px,0,0)`;
  };

  useLayoutEffect(() => {
    if (g.current.mode === 'h') return;
    setX(open ? 0 : width(), seen.current);
    seen.current = true;
  }, [open]);

  useEffect(() => {
    // A rotation changes how far "off screen" is; re-park the closed panel.
    const resize = (): void => {
      if (g.current.mode !== 'h' && !open) setX(width(), false);
    };
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [open]);

  const down =
    (fromSheet: boolean) =>
    (e: PointerEvent<HTMLDivElement>): void => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      // Suppression belongs only to the click generated by the previous swipe.
      // Reset even when this new touch will be handled by a row instead.
      suppressClick.current = false;
      const cur = g.current;
      cur.mode = 'ignore';
      if (fromSheet ? !open : open) return;
      if (!fromSheet && e.target instanceof Element && e.target.closest('.session-swipe')) {
        // Mid-row belongs to the row's own action swipe; the edge is ours.
        if (e.clientX < window.innerWidth - SHEET_EDGE) return;
      }
      cur.pointerId = e.pointerId;
      cur.mode = 'pending';
      cur.startX = e.clientX;
      cur.startY = e.clientY;
      cur.origin = fromSheet ? 0 : width();
      cur.x = cur.origin;
      cur.lastX = cur.origin;
      cur.lastT = e.timeStamp;
      cur.vx = 0;
    };

  const move = (e: PointerEvent<HTMLDivElement>): void => {
    const cur = g.current;
    if (cur.mode === 'idle' || cur.mode === 'ignore' || e.pointerId !== cur.pointerId) return;
    const dx = e.clientX - cur.startX;
    const dy = e.clientY - cur.startY;
    if (cur.mode === 'pending') {
      if (Math.abs(dx) < SHEET_SLOP && Math.abs(dy) < SHEET_SLOP) return;
      // Slightly diagonal still counts as a sheet swipe; only a clearly
      // vertical move, or the wrong horizontal direction, is a scroll.
      if (Math.abs(dy) > Math.abs(dx) * 1.35 || (cur.origin > 0 ? dx > 0 : dx < 0)) {
        cur.mode = 'ignore';
        return;
      }
      cur.mode = 'h';
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    const next = Math.max(0, Math.min(width(), cur.origin + dx));
    const dt = e.timeStamp - cur.lastT;
    if (dt > 0) cur.vx = (next - cur.lastX) / dt;
    cur.lastX = next;
    cur.lastT = e.timeStamp;
    setX(next, false);
  };

  const finish = (e: PointerEvent<HTMLDivElement>): void => {
    const cur = g.current;
    if (e.pointerId !== cur.pointerId) return;
    const wasSwipe = cur.mode === 'h';
    const { x, vx, origin } = cur;
    cur.mode = 'idle';
    cur.pointerId = -1;
    if (!wasSwipe) return;
    suppressClick.current = true;
    const w = width();
    const traveled = origin > 0 ? w - x : x;
    const pastCommit = traveled > w * SHEET_COMMIT;
    const shouldOpen =
      vx < -SHEET_FLICK ? true : vx > SHEET_FLICK ? false : origin > 0 ? pastCommit : !pastCommit;
    setX(shouldOpen ? 0 : w, true);
    setOpen(shouldOpen);
  };

  const close = useCallback(() => setOpen(false), []);

  const clickCapture = (e: MouseEvent<HTMLDivElement>): void => {
    if (!suppressClick.current) return;
    e.preventDefault();
    e.stopPropagation();
    suppressClick.current = false;
  };

  const handlers = (fromSheet: boolean): PaneGesture => ({
    onPointerDown: down(fromSheet),
    onPointerMove: move,
    onPointerUp: finish,
    onPointerCancel: finish,
    onClickCapture: clickCapture,
  });

  return {
    open,
    close,
    ref,
    paneHandlers: handlers(false),
    sheetHandlers: handlers(true),
  };
}

function SwipeRow({
  id,
  open,
  reorderable,
  label,
  onOpen,
  onClose,
  onBacklog,
  onDelete,
  onDropImages,
  children,
}: {
  id: string;
  open: boolean;
  reorderable: boolean;
  label: string;
  onOpen: () => void;
  onClose: () => void;
  onBacklog: () => void;
  onDelete: () => void;
  onDropImages?: (files: File[]) => void;
  children: ReactNode;
}): JSX.Element {
  const frontRef = useRef<HTMLDivElement>(null);
  const suppressClick = useRef(false);
  const seen = useRef(false);
  const drop = useImageDrop((files) => onDropImages?.(files), Boolean(onDropImages));
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: !reorderable || open,
  });
  const gesture = useRef({
    pointerId: -1,
    mode: 'idle' as 'idle' | 'pending' | 'h' | 'ignore',
    startX: 0,
    startY: 0,
    origin: 0,
    x: 0,
    lastX: 0,
    lastT: 0,
    vx: 0,
  });

  const setX = (x: number, animate: boolean): void => {
    const el = frontRef.current;
    if (!el) return;
    gesture.current.x = x;
    el.style.transition = animate ? 'transform 0.2s ease-out' : 'none';
    el.style.transform = `translate3d(${-x}px,0,0)`;
  };

  useLayoutEffect(() => {
    if (gesture.current.mode === 'h' || isDragging) return;
    setX(open ? HOME_ACTIONS : 0, seen.current);
    seen.current = true;
  }, [open, isDragging]);

  const rest = (): number => (open ? HOME_ACTIONS : 0);

  const onPointerDown = (e: PointerEvent<HTMLDivElement>): void => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    // A fresh touch must not inherit click suppression from the last swipe,
    // including touches at the edge reserved for the backlog sheet.
    suppressClick.current = false;
    if (
      e.target instanceof Element &&
      e.target.closest('.session-run, .session-action, .session-handle, .session-tag')
    ) {
      gesture.current.mode = 'ignore';
      return;
    }
    // The right edge opens the backlog panel instead of this row's actions.
    if (!open && e.clientX >= window.innerWidth - SHEET_EDGE) {
      gesture.current.mode = 'ignore';
      return;
    }
    const g = gesture.current;
    g.pointerId = e.pointerId;
    g.mode = 'pending';
    g.startX = e.clientX;
    g.startY = e.clientY;
    g.origin = rest();
    g.x = g.origin;
    g.lastX = g.origin;
    g.lastT = e.timeStamp;
    g.vx = 0;
  };

  const onPointerMove = (e: PointerEvent<HTMLDivElement>): void => {
    const g = gesture.current;
    if (g.mode === 'idle' || g.mode === 'ignore' || e.pointerId !== g.pointerId) return;
    const dx = e.clientX - g.startX;
    const dy = e.clientY - g.startY;
    if (g.mode === 'pending') {
      if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
      // Closed rows only care about a left swipe; anything else is a scroll.
      if (Math.abs(dy) >= Math.abs(dx) || (g.origin <= 0 && dx > 0)) {
        g.mode = 'ignore';
        return;
      }
      g.mode = 'h';
      e.currentTarget.setPointerCapture(e.pointerId);
      e.currentTarget.classList.add('is-dragging');
    }
    const width = e.currentTarget.offsetWidth || 360;
    const next = Math.max(0, Math.min(g.origin - dx, Math.max(HOME_ACTIONS, width * 0.92)));
    const dt = e.timeStamp - g.lastT;
    if (dt > 0) g.vx = (next - g.lastX) / dt;
    g.lastX = next;
    g.lastT = e.timeStamp;
    setX(next, false);
  };

  const finish = (e: PointerEvent<HTMLDivElement>): void => {
    const g = gesture.current;
    if (e.pointerId !== g.pointerId) return;
    const wasSwipe = g.mode === 'h';
    const x = g.x;
    const vx = g.vx;
    g.mode = 'idle';
    g.pointerId = -1;
    e.currentTarget.classList.remove('is-dragging');
    if (!wasSwipe) return;
    suppressClick.current = true;
    const flickOpen = vx > 0.4 && x > 20;
    const flickClose = vx < -0.4;
    const shouldOpen = flickOpen || (!flickClose && x > HOME_ACTIONS * 0.45);
    setX(shouldOpen ? HOME_ACTIONS : 0, true);
    if (shouldOpen) onOpen();
    else onClose();
  };

  return (
    <div
      ref={setNodeRef}
      className={`session-swipe${drop.over ? ' is-drop-target' : ''}${isDragging ? ' is-lifting' : ''}`}
      aria-hidden={isDragging || undefined}
      style={{
        transform: CSS.Translate.toString(transform),
        transition,
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={finish}
      onPointerCancel={finish}
      onClickCapture={(e) => {
        if (!suppressClick.current) return;
        e.preventDefault();
        e.stopPropagation();
        suppressClick.current = false;
      }}
      {...drop.handlers}
    >
      <div className="session-actions">
        <button
          type="button"
          className="session-action session-action-backlog"
          aria-label={`Move to backlog: ${label}`}
          onClick={onBacklog}
        >
          <Icon name="archive" size={20} strokeWidth={2.2} />
          Backlog
        </button>
        <button
          type="button"
          className="session-action session-action-delete"
          aria-label={`Delete chat: ${label}`}
          onClick={onDelete}
        >
          <Icon name="trash" size={20} strokeWidth={2.2} />
          Delete
        </button>
      </div>
      <div className="session" ref={frontRef}>
        {reorderable && (
          <button
            type="button"
            className="session-handle"
            aria-label={`Reorder chat: ${label}`}
            {...attributes}
            {...listeners}
            onPointerDown={(e) => {
              e.stopPropagation();
              listeners?.onPointerDown?.(e);
            }}
          >
            <Icon name="grip" size={18} strokeWidth={2.4} />
          </button>
        )}
        {children}
      </div>
    </div>
  );
}

function SessionOverlay({ session, now }: { session: Session; now: number }): JSX.Element {
  const { activeNodeRect } = useDndContext();
  const width = activeNodeRect?.width;
  return (
    <div className="session session-overlay" style={width ? { width } : undefined}>
      <span className="session-handle" aria-hidden="true">
        <Icon name="grip" size={18} strokeWidth={2.4} />
      </span>
      <SessionContents session={session} now={now} />
    </div>
  );
}

function PlusIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
      <path
        d="M12 5v14M5 12h14"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
    </svg>
  );
}
